import axios from 'axios';
import { isApiErrorCode, type ApiErrorParams } from '@designdeck/shared';
import i18n from '@/lib/i18n';

/**
 * Turns an API failure into text in the shop language.
 *
 * The API attaches a stable `code` (and resolved `params`) to errors a
 * seller may see; the message lives under `errors.<code>` in the locale
 * files. An error without a code falls back to a generic line for its
 * status, and anything that is not an API response — a network failure, a
 * thrown Error — keeps its own message, which is all there is.
 */

interface ApiErrorBody {
  statusCode?: number;
  message?: string | string[];
  code?: string;
  params?: ApiErrorParams;
}

function bodyOf(error: unknown): ApiErrorBody | null {
  if (!axios.isAxiosError(error)) return null;
  const data = error.response?.data;
  return typeof data === 'object' && data !== null
    ? (data as ApiErrorBody)
    : null;
}

/** The API error code, when the failure carries one. */
export function getApiErrorCode(error: unknown): string | undefined {
  const code = bodyOf(error)?.code;
  return isApiErrorCode(code) ? code : undefined;
}

/**
 * A translated message for a known code, or null when the code has no
 * translation. Exposed for the review page, which stores codes on its own.
 */
export function translateErrorCode(
  code: string | null | undefined,
  params?: ApiErrorParams
): string | null {
  if (!isApiErrorCode(code)) return null;
  const key = `errors.${code}`;
  return i18n.exists(key) ? i18n.t(key, { ...params }) : null;
}

export function describeApiError(error: unknown): string {
  const body = bodyOf(error);
  if (body) {
    const translated = translateErrorCode(body.code, body.params);
    if (translated) return translated;

    const status =
      body.statusCode ??
      (error as { response?: { status?: number } }).response?.status;
    const statusKey = `errors.status.${status}`;
    if (status && i18n.exists(statusKey)) return i18n.t(statusKey);
    return i18n.t('common.error');
  }
  if (axios.isAxiosError(error)) {
    // No response body at all: the request never reached the API.
    return i18n.t('errors.network');
  }
  if (error instanceof Error && error.message) return error.message;
  return i18n.t('common.error');
}

/**
 * A persisted publish failure reason: new rows hold a code, rows written
 * before the codes existed hold English text and are shown as they are.
 */
export function describePublishFailReason(
  reason: string | null | undefined
): string {
  if (!reason) return '';
  return translateErrorCode(reason) ?? reason;
}
